/**
 * Route Updates[] Handler
 *
 * Binds the original checkout button to a new empty form (no updates[] inputs)
 * so the cart's index-based updates[] array can't corrupt Route's quantity at
 * checkout.
 * 
 */
(function RouteUpdatesFormFix() {
  'use strict';

  // --- Configuration ---

  // Apply on every page, not just the cart page.
  const ALL_SURFACES = false;

  // Force a checkout button selector (null = read from Route's script tag).
  //   e.g. const BUTTON_SELECTOR = 'button.cart__checkout-button';
  const BUTTON_SELECTOR = null;

  // --- Internals ---

  const FORM_ID_BASE = 'route-updates-fix-form';

  const SEL = {
    widget:       '[data-route-widget]',        // paired layout
    widgetButton: '[data-route-widget-button]', // standard layout
    anyAnchor:    '[data-route-widget], [data-route-widget-button]',
  };

  let reinitTimer;
  const applied = []; // { button, formId } pairs, for reset()

  function log(msg, level = 'debug') {
    const prefix = '[Route Updates Fix]';
    if (level === 'error') console.error(prefix, msg);
    else if (level === 'warn') console.warn(prefix, msg);
    else console.debug(prefix, msg);
  }

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  // Reads checkoutSelector from Route's widget script tag URL params.
  // Handles both JSON ("entrypoints") and array-style ("entrypoints[0].checkoutSelector")
  // encoding — Zippy uses the former, Snappy uses the latter.
  function getRouteCheckoutSelectors() {
    const scripts = Array.from(document.querySelectorAll('script[src]'))
      .filter(s => s.src.includes('route') && s.src.includes('widget'));
    if (!scripts.length) return [];

    const selectors = [];

    for (const script of scripts) {
      try {
        const params = new URL(script.src).searchParams;
        const jsonVal = params.get('entrypoints');

        if (jsonVal) {
          const ep = JSON.parse(jsonVal);
          selectors.push(...ep.map(e => e.checkoutSelector).filter(Boolean));
        } else {
          for (const [key, value] of params) {
            if (/^entrypoints\[\d+\]\.checkoutSelector$/.test(key) && value) {
              selectors.push(value);
            }
          }
        }
      } catch (_) {}
    }

    return [...new Set(selectors)];
  }

  // Returns the box-generating ancestor of el, or el itself.
  // Needed because Route's standard layout wraps its anchor in display:contents,
  // which produces no box and always returns a zero rect.
  function getBoxAncestor(el) {
    if (getComputedStyle(el).display !== 'contents') return el;
    let ancestor = el.parentElement;
    while (ancestor) {
      if (getComputedStyle(ancestor).display !== 'contents') return ancestor;
      ancestor = ancestor.parentElement;
    }
    return null;
  }

  // Returns true for on-screen widget instances, false for off-canvas drawer copies.
  // Avoids checkVisibility() — it produces false negatives at readyState=interactive.
  function isVisible(el) {
    const boxEl = getBoxAncestor(el);
    if (!boxEl) return false;

    const rect = boxEl.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    if (rect.right <= 0 || rect.left >= window.innerWidth) return false;
    if (el.closest('[inert], [aria-hidden="true"]')) return false;

    const cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.opacity !== '0';
  }

  // Finds the original (pre-Route) checkout button relative to a widget anchor.
  // Tries Route's configured selector first, then a hidden button scan, then a
  // backwards sibling walk.
  function findOriginalButton(anchor, checkoutSelectors) {
    const notInWidget = ':not([data-route-widget] *):not([data-route-cloned-button])';
    const scope = anchor.closest('form') ?? anchor.parentElement;

    for (const sel of checkoutSelectors) {
      try {
        const btn = scope?.querySelector(sel + notInWidget);
        if (btn) return btn;

        const all = [...document.querySelectorAll(sel + notInWidget)];
        if (all.length === 1) return all[0];
        if (all.length > 1) {
          const match = all.find(b => b.closest('form') === anchor.closest('form'));
          if (match) return match;
        }
      } catch (_) {}
    }

    const hidden = Array.from(
      scope?.querySelectorAll(`button[name="checkout"]${notInWidget}`) ?? []
    ).find(b =>
      b.hasAttribute('data-original-display') ||
      b.style.display === 'none' ||
      window.getComputedStyle(b).display === 'none'
    );
    if (hidden) return hidden;

    let node = anchor.previousElementSibling;
    while (node) {
      if (node.matches?.(`button[name="checkout"]${notInWidget}`)) return node;
      const found = node.querySelector?.(`button[name="checkout"]${notInWidget}`);
      if (found) return found;
      node = node.previousElementSibling;
    }

    return null;
  }

  // Uses the literal action attribute rather than form.action's resolved URL.
  function getCartFormAction(button) {
    const closestAction = button.closest('form')?.getAttribute('action');
    if (closestAction) return closestAction;

    const associatedId = button.getAttribute('form');
    if (associatedId) {
      const associatedAction = document.getElementById(associatedId)?.getAttribute('action');
      if (associatedAction) return associatedAction;
    }

    return (
      document.querySelector('form[action*="/cart"]')?.getAttribute('action') ??
      `${window.Shopify?.routes?.root ?? '/'}cart`
    );
  }

  function buildFixForm(button, formId) {
    const form = document.createElement('form');
    form.id = formId;
    form.setAttribute('action', getCartFormAction(button));
    form.setAttribute('method', 'post');
    form.setAttribute('novalidate', '');
    form.setAttribute('aria-hidden', 'true');
    form.style.display = 'none';
    return form;
  }

  // Returns the element after which the fix form should be inserted.
  // Placed outside any existing form to avoid invalid nested form HTML.
  function getInsertionPoint(anchor, button) {
    return (
      button.closest('form') ??
      (button.getAttribute('form') ? document.getElementById(button.getAttribute('form')) : null) ??
      anchor
    );
  }

  function insertFix(anchor, button, index) {
    const formId = index === 0 ? FORM_ID_BASE : `${FORM_ID_BASE}-${index}`;
    if (document.getElementById(formId)) return;

    const insertionPoint = getInsertionPoint(anchor, button);
    if (!insertionPoint) {
      log(`Could not find insertion point for fix form (index ${index})`, 'warn');
      return;
    }

    const form = buildFixForm(button, formId);
    insertionPoint.insertAdjacentElement('afterend', form);
    button.setAttribute('form', formId);
    applied.push({ button, formId });

    log(`Applied: original checkout button → #${formId}`);
  }

  function reset() {
    applied.forEach(({ button, formId }) => {
      button.removeAttribute('form');
      document.getElementById(formId)?.remove();
    });
    applied.length = 0;
  }

  function applyToVisible() {
    if (!ALL_SURFACES && !isCartPage()) return;

    const anchors = [...document.querySelectorAll(SEL.anyAnchor)].filter(isVisible);
    if (!anchors.length) return;

    const selectors = BUTTON_SELECTOR ? [BUTTON_SELECTOR] : getRouteCheckoutSelectors();

    anchors.forEach((anchor, i) => {
      const button = findOriginalButton(anchor, selectors);
      if (!button) {
        log(`No original checkout button found for anchor at index ${i}`, 'warn');
        return;
      }
      insertFix(anchor, button, i);
    });
  }

  function scheduleReapply(delay = 50) {
    clearTimeout(reinitTimer);
    reinitTimer = setTimeout(() => { reset(); applyToVisible(); }, delay);
  }

  // Watches for Route widget injection and re-applies after each cart update.
  const observer = new MutationObserver(mutations => {
    const routeAnchorAdded = mutations.some(m =>
      [...m.addedNodes].some(n =>
        n.nodeType === Node.ELEMENT_NODE &&
        (n.matches?.(SEL.anyAnchor) || n.querySelector?.(SEL.anyAnchor))
      )
    );
    if (routeAnchorAdded) scheduleReapply(50);
  });

  // Handles bfcache restores and History API navigation where DOMContentLoaded
  // doesn't re-fire and the MutationObserver won't see a new anchor injection.
  function onPageResume(event) {
    const isBfcache = event.type === 'pageshow' && event.persisted;
    const isPopstate = event.type === 'popstate';
    if (!isBfcache && !isPopstate) return;
    if (isPopstate && !ALL_SURFACES && !isCartPage()) return;
    scheduleReapply(100);
  }

  function init() {
    if (document.querySelector(SEL.anyAnchor)) applyToVisible();
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('pageshow', onPageResume);
    window.addEventListener('popstate', onPageResume);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();